import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import BrightnessMediumIcon from '@mui/icons-material/BrightnessMedium';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import urls, { getProjectUrl } from '../../urls';
import projectService from '../../utils/apiRequests/projectService';
import { getProjectTypeConfig } from '../../utils/projectTypeConfig';
import useThemeToggle from '../../hooks/useThemeToggle';
import { useCommandSource } from './CommandPaletteContext';

/**
 * App-wide palette items available on every page while logged in:
 * project switching plus a few global actions. Mounted once inside the
 * CommandPaletteProvider.
 */
const GlobalCommands = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toggleTheme = useThemeToggle();

  useCommandSource('global', async () => {
    const items = [
      {
        id: 'global:dashboard',
        group: 'Go to',
        title: 'Dashboard',
        subtitle: 'All projects',
        keywords: 'home projects overview',
        icon: <DashboardIcon />,
        perform: () => navigate(urls.dashboard),
      },
      {
        id: 'global:settings',
        group: 'Go to',
        title: 'Settings',
        keywords: 'preferences account profile',
        icon: <SettingsIcon />,
        perform: () => navigate(urls.settings),
      },
      {
        id: 'global:toggle-theme',
        group: 'Actions',
        title: 'Toggle light / dark theme',
        keywords: 'theme dark mode light mode appearance',
        icon: <BrightnessMediumIcon />,
        perform: toggleTheme,
      },
    ];

    // Project switching from anywhere (offline-first cached list).
    try {
      const response = await projectService.getUserProjects({ useCache: true });
      const projects = response?.data?.projects || [];
      projects.forEach((project) => {
        const projectId = project._id;
        if (!projectId) return;
        const isCurrent = location.pathname.includes(`/projectDetails/${projectId}`);
        const typeConfig = getProjectTypeConfig(project.type);
        items.push({
          id: `global:project:${projectId}`,
          group: 'Projects',
          title: project.title || 'Untitled Project',
          subtitle: isCurrent ? `${typeConfig.label} • current project` : typeConfig.label,
          keywords: 'open project switch',
          icon: <FolderOpenIcon />,
          perform: () => navigate(getProjectUrl(projectId)),
        });
      });
    } catch {
      // Offline with a cold cache — project switching simply won't be listed.
    }

    return items;
  });

  return null;
};

export default GlobalCommands;
